import { Link } from "react-router-dom";
import { Mail, Reply } from "lucide-react";
import type { OutreachSender } from "@/services/outreach";

interface OutreachSenderPanelProps {
  sender: OutreachSender;
}

const PROFILE_NAME_LINK = "/profile?field=displayName";
const PROFILE_REPLY_LINK = "/profile?field=coachReplyEmail";

function nameNote(source: OutreachSender["nameSource"]): string | null {
  if (source === "profile") return null;
  if (source === "ranking") return "This is the name on your ranking profile.";
  if (source === "account") {
    return "This is the name on your account. If it's a parent's name, add the player's name on your profile.";
  }
  return "We don't have the player's name yet. Add it on your profile so coaches know who the email is from.";
}

/**
 * Shows how a coach email will go out before it's sent (ZON-291): the From
 * name coaches see, and where their replies land, with links to
 * change each on the profile page.
 */
const OutreachSenderPanel = ({ sender }: OutreachSenderPanelProps) => {
  const note = nameNote(sender.nameSource);
  const needsName = sender.nameSource === "account" || sender.nameSource === "fallback";

  return (
    <div
      className="rounded-md border border-border/60 bg-muted/30 px-3 py-2.5 text-sm space-y-2"
      data-testid="outreach-sender-panel"
    >
      <div className="flex items-start gap-2">
        <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-xs font-medium uppercase tracking-wide text-foreground/60">
              From
            </span>
            <span className="font-medium text-foreground">{sender.fromName}</span>
            <Link to={PROFILE_NAME_LINK} className="text-xs text-primary-text underline">
              Change name
            </Link>
          </div>
          {note && (
            <p
              className={`mt-0.5 text-xs ${needsName ? "text-amber-700" : "text-muted-foreground"}`}
            >
              {note}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2">
        <Reply className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-xs font-medium uppercase tracking-wide text-foreground/60">
              Replies go to
            </span>
            <span className="font-medium text-foreground break-all">{sender.replyTo}</span>
            <Link to={PROFILE_REPLY_LINK} className="text-xs text-primary-text underline">
              Change
            </Link>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {sender.replyToSource === "login"
              ? "This is your login email. To send coach replies to the player instead, add an email for coach replies on your profile."
              : "This is the email for coach replies on your profile."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OutreachSenderPanel;
